import React, { useContext, useEffect, useRef, useState } from 'react'
import DOMPurify from 'dompurify'
import AgentMessageGroup from './AgentMessageGroup'
import { makeStyles } from '@material-ui/core/styles'
import CalendarDrop from './CalendarDrop/CalendarDrop'
import RatingDrop from './RatingDrop'
import DateFormat from 'dateformat'
import RenderEmoji from './RenderEmoji'
import ChatOptionsPicker from './ChatOptionsPicker'
import InternetConnectionWarning from './InternetConnectionWarning'
import CTAButton from './CTAButton'
import { SessionContext } from './session-context'

const useStyles = makeStyles(theme => ({
  timeDiv: {
    display: 'flex',
    justifyContent: 'flex-end',
    fontFamily: 'Open Sans, sans serif',
    fontSize: '12px',
    color: '#7C7C7C',
    paddingRight: 10,
    paddingTop: 5
  },
  messageText: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: '4px 12px',
    borderRadius: 16,
    display: 'inline-block',
    right: 10
  }
}))

function ClientMessageRender (props) {
  const classes = useStyles()
  const row = props.message_event
  let contents
  if (row.attributes.kind === 'chat') {
    let body = row.attributes.body
    const parser = new DOMParser()
    const doc = parser.parseFromString(body, 'text/html')
    body = doc.body.textContent || ''
    contents = (
      <RenderEmoji
        key={row.id}
        className={classes.messageText}
        text={body}
      />
    )
  }

  return (
    <div className='visitor-message-group'>
      <div className='message-list'>
        {contents}
      </div>
    </div>
  )
}

function MessageDisplay (props) {
  const { welcomePacket } = useContext(SessionContext)
  const displayRef = useRef(null)
  const input_participants = props.participants

  const participants = {}
  input_participants.reduce((result, item) => {
    result[item.id] = item
    return result
  }, participants)

  const getConversationEvents = props.getConversationEvents
  const events = props.events
  const promptsToClosedPromptEvents = props.promptsToClosedPromptEvents
  const sendChat = props.sendChat
  const [, setRerender] = useState(false)

  function scrollToBottom (el) {
    el.scrollTop = el.scrollHeight - el.clientHeight
  }

  const visitorID = Object.values(participants).filter(p => p.attributes.kind === 'visitor').map(p => p.id)[0]

  const completedDrops = events.filter(event => {
    return event.attributes.kind === 'drop_complete'
  }).reduce((map, event) => {
    map[event.attributes.data.drop_id] = event
    return map
  }, {})

  const completedTextReminders = events.filter(event => {
    return event.attributes.kind === 'text_reminder'
  }).reduce((map, event) => {
    map[event.attributes.data.drop_id] = event
    return map
  }, {})

  const completedRatings = events.filter(event => {
    return event.attributes.kind === 'rating_complete'
  }).reduce((map, event) => {
    map[event.attributes.data.drop_id] = event
    return map
  }, {})

  const timeoutRef = useRef(null)

  useEffect(() => {
    const agentEvents = events.filter(event => event.relationships.from_participant.data.id !== visitorID)
    if (agentEvents.length) {
      const lastAgentEvent = agentEvents[agentEvents.length - 1]
      if (lastAgentEvent.attributes.kind === 'join' && lastAgentEvent.attributes.data.from === 'route') {
        const remainingTime = welcomePacket.fallback_timer * 1000 - (new Date() - new Date(lastAgentEvent.attributes.created_timestamp))
        timeoutRef.current = setTimeout(() => setRerender(true), remainingTime)
      }
    }
    return () => {
      clearTimeout(timeoutRef.current)
    }
  })

  function group_events (events, participants) {
    const output = []
    let current_group = {
      messages: [],
      participant_name: '',
      participant_kind: '',
      participant_id: '',
      avatarUrl: ''
    }
    let current_timestamp = null
    let last_from_id = null
    let event = null
    for (let i = 0; i < events.length; i++) {
      event = events[i]
      const participant_id =
        event.relationships.from_participant.data.id
      let participant = participants[participant_id]
      current_timestamp = event.attributes.created_timestamp
      if (!participant) {
        participant = {
          attributes: {
            kind: 'agent',
            avatarUrl: ''
          }
        }
      }

      if (event.attributes.kind === 'option_prompt') {
        if (current_group.messages.length) {
          output.push(current_group)
          current_group = {
            messages: [],
            participant_name: '',
            participant_kind: '',
            participant_id: '',
            avatarUrl: ''
          }
        }
        last_from_id = null
        output.push({
          type: 'option_prompt',
          event_id: event.id,
          timestamp: current_timestamp,
          data: event
        })
        continue
      }

      if (event.attributes.kind === 'option_prompt_free') {
        if (current_group.messages.length) {
          output.push(current_group)
          current_group = {
            messages: [],
            participant_name: '',
            participant_kind: '',
            participant_id: '',
            avatarUrl: ''
          }
        }
        last_from_id = null
        output.push({
          type: 'option_prompt_free',
          event_id: event.id,
          timestamp: current_timestamp,
          data: event
        })
        continue
      }

      if (event.attributes.kind === 'cta_button') {
        if (current_group.messages.length) {
          output.push(current_group)
          current_group = {
            messages: [],
            participant_name: '',
            participant_kind: '',
            participant_id: '',
            avatarUrl: ''
          }
        }
        last_from_id = null
        output.push({
          type: 'cta_button',
          event_id: event.id,
          timestamp: current_timestamp,
          data: event
        })
        continue
      }

      if (participant_id !== last_from_id || event.attributes.kind === 'calendar_drop' || event.attributes.kind === 'rating_drop') {
        if (current_group.messages.length) {
          output.push(current_group)
        }
        current_group = {
          participant_name: event.attributes.participant_name,
          participant_kind: participant.attributes.kind,
          participant_id: participant_id,
          messages: [],
          timestamp: current_timestamp,
          avatarUrl: participant.attributes.avatar_url,
          typing: false
        }
        last_from_id = participant_id
        if (event.attributes.kind === 'calendar_drop') {
          const calendar_drop_event = {
            type: 'calendar_drop',
            event_id: event.id,
            user_id: participant.attributes.user_id,
            timestamp: current_timestamp,
            data: event.attributes.data
          }
          output.push(calendar_drop_event)
          last_from_id = null
          continue
        } else if (event.attributes.kind === 'rating_drop') {
          const rating_drop_event = {
            type: 'rating_drop',
            event_id: event.id,
            user_id: participant.attributes.user_id,
            timestamp: current_timestamp,
            data: event.attributes.data
          }
          output.push(rating_drop_event)
          last_from_id = null
          continue
        } else if (event.attributes.kind === 'fallback') {
          const fallback_event = {
            type: 'fallback',
            event_id: event.id,
            timestamp: current_timestamp,
            messages: [event],
            agent_name: event.attributes.participant_name
          }
          output.push(fallback_event)
          last_from_id = null
          continue
        }
      }
      if (event.attributes.kind === 'type') {
        current_group.typing = true
      } else {
        current_group.messages.push(event)
        current_group.timestamp = current_timestamp
      }
    }

    if (current_group.messages.length || (event && event.attributes.kind === 'type')) {
      output.push(current_group)
    }
    return output
  }

  useEffect(() => {
    if (displayRef.current && props.unconnected) {
      scrollToBottom(displayRef.current)
    }
  }, [props.unconnected])

  useEffect(() => {
    if (displayRef.current) {
      scrollToBottom(displayRef.current)
    }
  }, [events.length, props.currentTypingEvent])

  const missing_participant_id_count = events.reduce((a, event) => {
    const participant = participants[event.relationships.from_participant.data.id]
    if (!participant) {
      return a + 1
    }
    return a
  }, 0)

  useEffect(() => {
    if (missing_participant_id_count) {
      getConversationEvents()
    }
    // eslint-disable-next-line
  }, [events.length])

  if (missing_participant_id_count) {
    return (
      <div id='messages' ref={displayRef} data-missing='yes' />
    )
  }

  let welcomeMessage = ''
  let agentName = ''

  function MsgTime (props) {
    const classes = useStyles()

    return (
      <div className={classes.timeDiv}>
        <div>
          {DateFormat(props.timestamp, 'h:MM TT')}
        </div>
      </div>
    )
  }

  const filtered_events = events.filter(event => ['chat', 'calendar_drop', 'attachment', 'option_prompt', 'option_prompt_free', 'rating_drop', 'cta_button'].includes(event.attributes.kind))

  if (props.currentTypingEvent) {
    filtered_events.push(props.currentTypingEvent)
  }

  const grouped_events = group_events(filtered_events, participants)

  let meta = null
  let agent_name = null

  if (props.activeConversation) {
    meta = props.activeConversation.attributes.meta
    agent_name = meta.first_send_type === 'user' ? meta.first_agent_name : meta.bot_name
  }

  if (props.loading) {
    return <></>
  }

  if (props.activeConversation && meta.message_prompt) {
    welcomeMessage = meta.message_prompt
    agentName = agent_name
  }

  return (
    <div id='messages' ref={displayRef}>
      <br />
      <>
        {welcomeMessage ? (
          <AgentMessageGroup
            key='prompt'
            messages={[
              { id: 'prompt', attributes: { body: welcomeMessage } }
            ]}
            agent_name={agentName}
            bot={false}
            avatarUrl={props.botAvatar}
          />
        ) : (
          <></>
        )}
      </>

      <>
        {grouped_events.map((group) => {
          if (group.type === 'calendar_drop') {
            return (
              <CalendarDrop
                event={group}
                key={group.event_id}
                completedDrops={completedDrops}
                completedTextReminders={completedTextReminders}
              />
            )
          }

          if (group.type === 'rating_drop') {
            return (
              <RatingDrop
                event_id={group.event_id}
                key={group.event_id}
                ratingType={group.data.rating_type}
                completedRatings={completedRatings}
              />
            )
          }

          if (group.type === 'fallback') {
            return (
              <div style={{ marginBottom: 7 }} key={group.event_id}>
                <AgentMessageGroup
                  messages={group.messages}
                  agent_name={group.agent_name}
                  avatarUrl={props.botAvatar}
                  bot={false}
                  typing={group.typing}
                />
              </div>
            )
          }

          if (group.type === 'cta_button') {
            return (
              <CTAButton
                key={group.event_id}
                conversation_event={group.data}
              />
            )
          }

          if (group.type === 'option_prompt' || group.type === 'option_prompt_free') {
            return (
              <ChatOptionsPicker
                key={group.event_id}
                conversation_event={group.data}
                options={group.data.attributes.data.options}
                completedDrops={completedDrops}
                promptsToClosedPromptEvents={promptsToClosedPromptEvents}
                chooseValue={(value, prompt, options) => {
                  sendChat(value, {
                    bot_response: {
                      flow_id: prompt.attributes.data.bot.flow_id,
                      node_id: prompt.attributes.data.bot.node_id,
                      consent_event_id: prompt.attributes.data.bot.consent_event_id,
                      response_to_event_id: prompt.id,
                      options: options
                    }
                  })
                }}
              />
            )
          }
          if (['agent', 'bot'].includes(group.participant_kind)) {
            const key = group.messages.length ? group.messages[0].id : 0
            const isCloudAgent = group.participant_id === props.botParticipantID && props.cloudAgentName && props.cloudAgentAvatar
            return (
              <AgentMessageGroup
                key={key}
                messages={group.messages}
                agent_name={isCloudAgent ? props.cloudAgentName : group.participant_name}
                avatarUrl={isCloudAgent ? props.cloudAgentAvatar : group.avatarUrl}
                typing={group.typing}
              />
            )
          } else {
            return (
              <div key={group.messages[0].id} style={{ padding: '5px 0px 0px 0px' }}>
                {group.messages.map(row => (
                  <ClientMessageRender
                    key={row.id}
                    message_event={row}
                  />
                ))}
                <MsgTime timestamp={group.timestamp} />
              </div>
            )
          }
        })}
      </>
      <br />
      <div style={{ display: props.unconnected ? null : 'none' }}>
        <InternetConnectionWarning />
      </div>
    </div>
  )
}

export default MessageDisplay
